/**
 * Accordion
 *
 * <div data-accordion="single|multiple">
 *   <div data-accordion-item class="is-open">
 *     <button data-accordion-trigger aria-expanded="true" aria-controls="id">…</button>
 *     <div data-accordion-panel id="id" class="accordion-panel"><div>…</div></div>
 *   </div>
 * </div>
 *
 * The open state is the `.is-open` class on the item (CSS animates the panel
 * with grid-template-rows). `single` closes the other items of the same
 * accordion. Closed panels are made `inert` so they are neither focusable nor
 * read by assistive technology. A single card without a container works too:
 * just use data-accordion-item on its own.
 *
 * After the opening transition the item is scrolled into view (see `reveal`),
 * so an item near the bottom edge of a section does not open into the area
 * below the fold — the visitor would click and see nothing change.
 */
import { freezeScroll, scrollToY } from './scroll'

/** Breathing room below the opened item. */
const GAP = 24

let counter = 0

const setOpen = (item, open) => {
  const trigger = item.querySelector('[data-accordion-trigger]')
  const panel = item.querySelector('[data-accordion-panel]')
  item.classList.toggle('is-open', open)
  trigger?.setAttribute('aria-expanded', String(open))
  if (panel) {
    panel.inert = !open
  }
  item.dispatchEvent(new CustomEvent('accordion:toggle', { bubbles: true, detail: { open } }))
}

/** Resolve once the panel has finished growing, so the reveal measures the final layout. */
const opened = (panel) =>
  new Promise((resolve) => {
    const done = () => {
      panel.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      // A frame later: a section that grew has had its scroll compensation (stack.js).
      window.requestAnimationFrame(() => resolve())
    }
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'grid-template-rows') done()
    }
    panel.addEventListener('transitionend', onEnd)
    const timer = setTimeout(done, 600)
  })

/**
 * Scroll the opened item into view — only as far as needed, never past the
 * freeze of its section (the next section would slide over the panel) and
 * never so far that the trigger itself disappears under the header.
 */
const reveal = (item, trigger) => {
  const hidden = item.getBoundingClientRect().bottom + GAP - window.innerHeight
  if (hidden < 1) return

  const header = document.querySelector('[data-header]')
  const headroom =
    trigger.getBoundingClientRect().top - (header?.getBoundingClientRect().bottom ?? 0)
  const limit = freezeScroll(item.closest('[data-section]'))
  const top = Math.min(window.scrollY + Math.min(hidden, Math.max(0, headroom)), limit)

  if (top - window.scrollY > 1) scrollToY(top)
}

document.querySelectorAll('[data-accordion-item]').forEach((item) => {
  const trigger = item.querySelector('[data-accordion-trigger]')
  const panel = item.querySelector('[data-accordion-panel]')
  if (!trigger || !panel) return

  if (!panel.id) {
    panel.id = `accordion-panel-${++counter}`
  }
  trigger.setAttribute('aria-controls', panel.id)
  setOpen(item, item.classList.contains('is-open'))

  trigger.addEventListener('click', () => {
    const open = !item.classList.contains('is-open')
    const accordion = item.closest('[data-accordion]')

    if (open && accordion?.dataset.accordion === 'single') {
      accordion.querySelectorAll('[data-accordion-item].is-open').forEach((other) => {
        if (other !== item && other.closest('[data-accordion]') === accordion) {
          setOpen(other, false)
        }
      })
    }

    setOpen(item, open)

    if (open) {
      // A closed panel is a 0fr grid row: it clips visually, but the images
      // inside keep their full rect, so the deeper rows of a long list measure
      // thousands of px below the section and no lookahead reaches them. Ask
      // lazysizes to look again — now, for the rows at the top of the panel,
      // and once more when the panel has its real height (src/js/lazysizesConfig.js).
      const recheck = () => window.lazySizes?.loader?.checkElems()
      recheck()
      opened(panel).then(() => {
        recheck()
        reveal(item, trigger)
      })
    }
  })
})
